/* Overview: why they are here, who knows them, and how they are actually training. */
import type { Member } from '@acrostic/model';
import { ANCHOR_DECAY_DAYS, anchorStrength, daysSince } from '@acrostic/model';
import { useState } from 'react';
import { Panel, NoneRow } from '../../components/Panel.js';
import { useToast } from '../../components/Toast.js';
import { AnchorCapture } from '../home/AnchorCapture.js';
import { useSeesCommercials, useSeesScores, useSource } from '../../data/SourceProvider.js';
import { fmt } from '../../lib/fmt.js';
import { Scores } from './Scores.js';
import { gymToday } from '../../lib/today.js';

function Facts({ m, money }: { m: Member; money: boolean }) {
  const facts: [string, string][] = [
    ['Last visit', fmt.ago(m.lastVisit)],
    ['Visits (30 days)', String(m.visits30d)],
    ['Avg visits / week', m.avgVisitsWk.toFixed(1)],
    ['Was', m.priorVisitsWk == null ? '—' : `${m.priorVisitsWk.toFixed(1)} / week`],
    ['Joined', fmt.date(m.joinedAt)],
    /* The renewal date is a commercial fact and goes with the rest of them. How long
       they have been here is not, and stays. */
    ...(money ? [['Renewal', fmt.date(m.membershipExpires)] as [string, string]] : []),
  ];
  return (
    <div className="facts">
      {facts.map(([k, v]) => (
        <div className="fact" key={k}>
          <div className="t-label-medium">{v}</div>
          <div className="t-caption muted">{k}</div>
        </div>
      ))}
    </div>
  );
}

/* SITE-RULES 2. An anchor is a NAMED, reciprocal relationship, and the whole point is
   that it is a person rather than a count. Strength is derived from the last two-way
   contact, never stored: a stored "strong" that nothing writes back down is an anchor
   that never ages. */
/* AND NOW IT CAN BE WRITTEN FROM HERE.
 *
 * Six screens read anchors; one screen could record one, and it was the Support Coach
 * console - not the member's own record, which is where somebody standing in front of
 * that member actually is. So on every other seat, and from the one page about this
 * person, the product's central idea was read-only.
 *
 * Three verbs, because an anchor is a relationship rather than a field: name one,
 * say you have spoken to them again, and say it has ended. The middle one is the one
 * that keeps the model honest - strength is derived from the last two-way contact, so
 * an anchor nobody ever touches decays on its own and eventually stops counting. */
function Anchors({ m, onChanged }: { m: Member; onChanged: () => void }) {
  const source = useSource();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState('');

  const spoke = async (name: string) => {
    setBusy(name);
    try {
      await source.touchMemberAnchor(m.id, name);
      toast(`Noted: you spoke to ${name}`, 'good');
      onChanged();
    } catch (e) {
      toast(e instanceof Error ? e.message : 'That did not save', 'bad');
    } finally { setBusy(''); }
  };

  const remove = async (name: string) => {
    setBusy(name);
    try {
      await source.removeMemberAnchor(m.id, name);
      toast(`${name} is no longer an anchor`, 'good');
      onChanged();
    } catch (e) {
      toast(e instanceof Error ? e.message : 'That did not save', 'bad');
    } finally { setBusy(''); }
  };

  return (
    <>
      {!m.anchors.length ? (
        <NoneRow>
          Nobody. Assigned to a coach is a name on a list, not a person who knows them.
        </NoneRow>
      ) : (
        <div className="rows">
          {m.anchors.map((a) => {
            const strong = anchorStrength(a) === 'strong';
            const d = daysSince(a.lastTwoWay, gymToday());
            return (
              <div className="row" key={a.name}>
                <span className={'dot ' + (strong ? 'good' : 'warn')} aria-hidden="true" />
                <div className="who">
                  <div className="n t-label-large">{a.name}</div>
                  {/* The basis is what makes it two-way. Without it this is an assignment. */}
                  <div className="s t-body-small">{a.basis}</div>
                </div>
                <div className="t-caption muted" style={{ whiteSpace: 'nowrap' }}>
                  {d == null ? 'never' : `${d}d ago`}
                  {!strong && ` · over ${ANCHOR_DECAY_DAYS}d`}
                </div>
                <div style={{ whiteSpace: 'nowrap' }}>
                  <button
                    type="button" className="btn t-label-small" disabled={!!busy}
                    onClick={() => void spoke(a.name)}
                  >Spoke again</button>{' '}
                  <button
                    type="button" className="btn t-label-small" disabled={!!busy}
                    onClick={() => void remove(a.name)}
                  >Remove</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: 10 }}>
        <button type="button" className="btn t-label-small" onClick={() => setAdding(true)}>
          Who knows them?
        </button>
      </div>

      {adding && (
        <AnchorCapture
          m={m}
          onClose={() => setAdding(false)}
          onDone={() => { setAdding(false); onChanged(); }}
        />
      )}
    </>
  );
}

export function Overview({ m, onChanged }: { m: Member; onChanged: () => void }) {
  const money = useSeesCommercials();
  const seesScores = useSeesScores();
  return (
    <>
      <Panel eyebrow="In their words" title="Why they are here">
        {m.why
          ? <p className="t-body-large">{m.why}</p>
          : <NoneRow>Not recorded. This is the one thing worth asking on their next visit.</NoneRow>}
        {m.goal && <p className="t-body-small muted" style={{ marginTop: 8 }}>Goal: {m.goal}</p>}
      </Panel>

      {/* THE ARITHMETIC, for the seats that see numbers at all. PRD 4 puts the
          five and their breakdown on Owner and Manager; the floor gets the order
          and the reason in plain words and no number, so this is not rendered
          there. `seesScores` is the same question `useSeesCommercials` asks one
          field over. */}
      {seesScores && m.risk?.scores && <Scores scores={m.risk.scores} />}

      <Panel eyebrow="Named, never counted" title={`Who knows ${m.firstName}`}>
        <Anchors m={m} onChanged={onChanged} />
      </Panel>

      <Panel eyebrow="How they are training" title="Attendance">
        <Facts m={m} money={money} />
        {m.risk?.why?.length ? (
          <ul className="whylist t-body-small">
            {m.risk.why.map((w) => <li key={w}>{w}</li>)}
          </ul>
        ) : null}
      </Panel>
    </>
  );
}
